import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Heart, X, Shield } from 'lucide-react';
import { useProfile } from '../context/ProfileContext';
import { localISO } from '../data/programs';
import { painAdjustment } from '../utils/training-analytics';

/**
 * PainCheckCard — reusable daily pain check display + editor.
 *
 * Dashboard surface: shows today's pain level + recommendation, with a tap
 * target to open the inline editor modal. Identical edit-in-place pattern
 * we use on the Strength page banner.
 *
 * Backed by profile.painLog so it persists across navigation and reflects
 * the latest value everywhere instantly.
 *
 * GDPR Art. 9 first-use consent: new users see a one-tap acknowledgement
 * before their first pain log is recorded. Existing users with prior pain
 * data are silently auto-confirmed on mount — no visible interruption.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HEALTH_CONSENT_KEY = 'enferno_health_consent_v1';

const recordHealthConsent = () => {
  try { localStorage.setItem(HEALTH_CONSENT_KEY, '1'); } catch { /* ignore */ }
  axios.patch(`${API}/account/consents`, { health_data_collection: true }).catch(() => {});
};

const PainCheckCard = () => {
  const { profile, logPain } = useProfile();
  const today = localISO(new Date());
  const todayEntry = (profile.painLog || {})[today];
  const todayLevel = todayEntry?.level ?? null;
  // Conservative banding (mid band smallest) applies to the chronic-pain submode
  // only — mirrors Strength + backend so the editor's tier copy matches the
  // badge/banner everywhere.
  const conservative = (profile.adaptiveSubmodes || []).includes('chronic-pain');
  const todayResult = todayLevel !== null ? painAdjustment(todayLevel, conservative) : null;

  const [showConsent, setShowConsent] = useState(false);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(todayLevel ?? 0);

  // Existing users who already have pain data are silently auto-confirmed so
  // they never see the intercept for a feature they're already using.
  useEffect(() => {
    const hasPriorData = Object.keys(profile.painLog || {}).length > 0;
    const alreadyConsented = (() => { try { return !!localStorage.getItem(HEALTH_CONSENT_KEY); } catch { return true; } })();
    if (hasPriorData && !alreadyConsented) recordHealthConsent();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const start = () => {
    setDraft(todayLevel ?? 0);
    const alreadyConsented = (() => { try { return !!localStorage.getItem(HEALTH_CONSENT_KEY); } catch { return true; } })();
    const hasPriorData = Object.keys(profile.painLog || {}).length > 0;
    if (!alreadyConsented && !hasPriorData) {
      setShowConsent(true);
    } else {
      setOpen(true);
    }
  };

  const confirmConsent = () => {
    recordHealthConsent();
    setShowConsent(false);
    setOpen(true);
  };

  const save = () => {
    logPain(draft);
    setOpen(false);
  };

  // Not-yet-logged state — invitational, never shame-y
  if (todayLevel === null) {
    return (
      <>
        <button
          type="button"
          onClick={start}
          data-testid="pain-check-card-empty"
          className="w-full text-left p-4 rounded-xl bg-enf-surface-deep border border-enf-surface-border hover:border-enf-surface-border transition-colors flex items-start gap-3"
        >
          <Heart className="w-4 h-4 text-enf-role-info mt-0.5" />
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-enf-text-secondary font-semibold mb-1">Pain check</div>
            <div className="text-sm text-enf-text-secondary">How's your body feeling today? Tap to log.</div>
          </div>
        </button>
        {showConsent && <HealthConsentModal onConfirm={confirmConsent} onClose={() => setShowConsent(false)} />}
        {open && <Modal draft={draft} setDraft={setDraft} onSave={save} onClose={() => setOpen(false)} conservative={conservative} />}
      </>
    );
  }

  const tierClass =
    todayResult.tier === 'red' ? 'bg-enf-role-danger/10 border-enf-role-danger/40 hover:bg-enf-role-danger/15' :
    todayResult.tier === 'amber' ? 'bg-enf-role-warning/5 border-enf-role-warning/30 hover:bg-enf-role-warning/10' :
    'bg-enf-surface-special/30 border-enf-border-special/30 hover:bg-enf-surface-special/40';
  const iconClass =
    todayResult.tier === 'red' ? 'text-enf-role-danger' :
    todayResult.tier === 'amber' ? 'text-enf-role-warning' : 'text-enf-role-info';

  return (
    <>
      <button
        type="button"
        onClick={start}
        data-testid="pain-check-card"
        aria-label="Edit today's pain level"
        className={`w-full text-left p-4 rounded-xl border flex items-start gap-3 transition-colors ${tierClass}`}
      >
        <Heart className={`w-4 h-4 mt-0.5 ${iconClass}`} />
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest font-semibold mb-1 text-enf-text-secondary">Pain check · {todayLevel}/10 · tap to edit</div>
          <div className="text-sm text-enf-text-primary">{todayResult.message}</div>
        </div>
      </button>
      {showConsent && <HealthConsentModal onConfirm={confirmConsent} onClose={() => setShowConsent(false)} />}
      {open && <Modal draft={draft} setDraft={setDraft} onSave={save} onClose={() => setOpen(false)} conservative={conservative} />}
    </>
  );
};

const HealthConsentModal = ({ onConfirm, onClose }) => (
  <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="health-consent-modal">
    <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl max-w-md w-full p-6">
      <div className="flex items-start justify-between mb-4">
        <Shield className="w-5 h-5 text-enf-role-info mt-0.5" />
        <button type="button" onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary" aria-label="Close">
          <X className="w-5 h-5" />
        </button>
      </div>
      <div className="font-display text-2xl tracking-wide leading-snug mb-2">Your body data stays with you</div>
      <p className="text-sm text-enf-text-secondary leading-relaxed mb-5">
        Your pain check-ins help your coach meet your body where it is — easing the work on harder days, never pushing you through one.
        What you log stays private: never sold, never shared with anyone.
        You can delete everything, anytime, in Profile.
      </p>
      <button
        type="button"
        onClick={onConfirm}
        data-testid="health-consent-confirm"
        className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors"
      >
        Got it — let's check in
      </button>
    </div>
  </div>
);

const Modal = ({ draft, setDraft, onSave, onClose, conservative = false }) => {
  const r = painAdjustment(draft, conservative);
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="pain-modal">
      <div className="bg-enf-surface-deep border border-enf-surface-border rounded-xl max-w-md w-full p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="font-display text-3xl tracking-wide leading-none mb-1">How's your body today?</div>
            <div className="text-enf-text-secondary text-sm">0 = no pain · 10 = severe. Update anytime.</div>
          </div>
          <button type="button" onClick={onClose} className="text-enf-text-muted hover:text-enf-text-primary" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <input
          type="range" min="0" max="10" value={draft}
          onChange={(e) => setDraft(Number(e.target.value))}
          className="w-full accent-enf-accent"
          data-testid="pain-slider"
        />
        <div className="flex items-center justify-between mt-2 mb-4">
          <span className="text-xs text-enf-text-muted">No pain</span>
          <span className="font-display text-4xl tracking-wide" data-testid="pain-value">{draft}</span>
          <span className="text-xs text-enf-text-muted">Severe</span>
        </div>

        <div className={`p-3 rounded-md border text-sm mb-4 ${
          r.tier === 'red' ? 'bg-enf-role-danger/10 border-enf-role-danger/40 text-enf-role-danger' :
          r.tier === 'amber' ? 'bg-enf-role-warning/5 border-enf-role-warning/30 text-enf-role-warning' :
          'bg-enf-surface-special/30 border-enf-border-special/30 text-enf-text-secondary'
        }`}>
          {r.message}
        </div>

        <button
          type="button"
          onClick={onSave}
          data-testid="pain-save"
          className="w-full py-3 bg-enf-accent hover:bg-enf-accent-hover rounded-md font-semibold text-sm transition-colors"
        >
          Save pain level
        </button>
      </div>
    </div>
  );
};

export default PainCheckCard;
